// src/pages/registeredUsers/tabs/PostAd.tsx

import React, {useState, useCallback, useEffect } from 'react';
import {useNavigate } from 'react-router-dom';

// Font Awesome Imports
import {FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faPlus } from '@fortawesome/free-solid-svg-icons';

// Firebase Imports
import {
  collection,
  doc,
  setDoc,
  Timestamp,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import {ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {db, storage } from '../../../firebase';

// Styled components imports
import {
  ContentSection,
  FormGroup,
  Label,
  Input,
  Select,
  Button,
  MediaUploadContainer,
  MediaPreview,
  FormSection,
  TextArea,
} from '../styles/listings.style';

// Your type definitions
import {FormMediaItem, ListingMedia } from '../../../types/listing';

const mediaTypes = [
  {value: '', label: 'Select Media Type' },
  {value: 'item_image', label: 'Item Image (Description, Price, Image)' },
  {value: 'video', label: 'Video (Description, 1-minute Video(100MBs))' },
  {value: 'general_image', label: 'General Image (Description, Image)' },
];

// Categories that require NID and Title/Logbook
const VERIFICATION_CATEGORIES = ['land', 'real_estate', 'cars', 'vehicles', 'automotive'];

interface PostAdProps {
    userId: string;
    userStallNumber: string;
    stallDetails: any; // Use a more specific type if you have one
    onPostSuccess: () => void;
}

const PostAd = ({userId, userStallNumber, stallDetails, onPostSuccess }: PostAdProps) => {
    // Form and Media-specific state
    const [selectedMediaType, setSelectedMediaType] = useState('');
    const [itemImageDescription, setItemImageDescription] = useState('');
    const [itemImagePrice, setItemImagePrice] = useState('');
    const [videoDescription, setVideoDescription] = useState('');
    const [generalImageDescription, setGeneralImageDescription] = useState('');
    
    // Verification documents state
    const [nidFile, setNidFile] = useState<File | null>(null);
    const [titleOrLogbookFile, setTitleOrLogbookFile] = useState<File | null>(null);
    const [verificationStatus, setVerificationStatus] = useState<'pending' | 'verified' | 'not_required'>('not_required');
    const [stallVerification, setStallVerification] = useState<{
      status: 'pending' | 'verified' | null;
      nidUrl: string | null;
      titleUrl: string | null;
    }>({ status: null, nidUrl: null, titleUrl: null });

    const [uploadedMedia, setUploadedMedia] = useState<FormMediaItem[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();
    const [postSuccess, setPostSuccess] = useState(false);

    // Determine limits based on stall type
    const isPremium = stallDetails?.stallType === 'Premium';
    const maxImages = isPremium ? 30 : 20;
    const maxVideos = isPremium ? 10 : 3;

    // Check if current category requires verification
    const requiresVerification = VERIFICATION_CATEGORIES.includes(
        stallDetails?.category?.toLowerCase() || ''
    );

    // Fetch stall verification status on component load
    useEffect(() => {
        const fetchStallVerification = async () => {
            if (userStallNumber) {
                const stallDocRef = doc(db, 'stalls', userStallNumber);
                const stallSnap = await getDoc(stallDocRef);
                if (stallSnap.exists()) {
                    const data = stallSnap.data();
                    setStallVerification({
                        status: data.verificationStatus || null,
                        nidUrl: data.nidDocumentUrl || null,
                        titleUrl: data.titleOrLogbookDocumentUrl || null,
                    });
                }
            }
        };

        if (requiresVerification) {
            fetchStallVerification();
        }
    }, [userStallNumber, requiresVerification]);

    const handleMediaTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedMediaType(e.target.value);
        setItemImageDescription('');
        setItemImagePrice('');
        setVideoDescription('');
        setGeneralImageDescription('');
        setFileInputKey(prevKey => prevKey + 1);
        setError(null);
    };

    const handleFinishPosting = () => {
    navigate('/');
    };

    const handleVerificationFileChange = (
        e: React.ChangeEvent<HTMLInputElement>, 
        type: 'nid' | 'title_logbook'
    ) => {
        const file = e.target.files ? e.target.files[0] : null;
        if (!file) {
            if (type === 'nid') setNidFile(null);
            if (type === 'title_logbook') setTitleOrLogbookFile(null);
            return;
        }

        // Validate file size (5MB max for documents)
        const maxFileSize = 5 * 1024 * 1024; // 5MB
        
        if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
            setError("Please upload an image (JPEG, PNG) or PDF file.");
            e.target.value = '';
            if (type === 'nid') setNidFile(null);
            if (type === 'title_logbook') setTitleOrLogbookFile(null);
            return;
        }
        
        if (file.size > maxFileSize) {
            setError("File is too large (max 5MB).");
            e.target.value = '';
            if (type === 'nid') setNidFile(null);
            if (type === 'title_logbook') setTitleOrLogbookFile(null);
            return;
        }

        if (type === 'nid') {
            setNidFile(file);
        } else {
            setTitleOrLogbookFile(file);
        }
        setError(null);
    };

    const handleMediaFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'item_image' | 'video' | 'general_image') => {
        const file = e.target.files ? e.target.files[0] : null;
        if (!file) {
            return;
        }

        // Validate descriptions/prices before processing file
        if (type === 'item_image') {
            if (!itemImageDescription.trim()) {
                setError("Please enter an item description before selecting the image.");
                e.target.value = '';
                return;
            }
            if (!itemImagePrice) {
                setError("Please enter a price before selecting the image.");
                e.target.value = '';
                return;
            }
        } else if (type === 'video') {
            if (!videoDescription.trim()) {
                setError("Please enter a video description before selecting the video.");
                e.target.value = '';
                return;
            }
        } else if (type === 'general_image') {
            if (!generalImageDescription.trim()) {
                setError("Please enter a description before selecting the image.");
                e.target.value = '';
                return;
            }
        }

        const addMedia = (fileToAdd: File) => {
             const newMediaItem: FormMediaItem = {
                id: `${type === 'video' ? 'vid' : 'img'}-${Date.now()}`,
                url: URL.createObjectURL(fileToAdd),
                file: fileToAdd,
                type: type === 'video' ? 'video' : 'image',
                description: type === 'item_image' ? itemImageDescription : (type === 'video' ? videoDescription : generalImageDescription),
                price: type === 'item_image' ? Number(itemImagePrice) : undefined,
            };

            setUploadedMedia(prev => [...prev, newMediaItem]);
            // Clear inputs
            setItemImageDescription('');
            setItemImagePrice('');
            setVideoDescription('');
            setGeneralImageDescription('');
            setFileInputKey(prev => prev + 1);
            setError(null);
        }

        // Validate file size (10MB max for images, 100MB for videos)
        const maxImageSize = 10 * 1024 * 1024; // 10MB
        const maxVideoSize = 100 * 1024 * 1024; // 100MB
        
        if (type === 'video') {
            if (!file.type.startsWith('video/')) {
                setError("Please upload a video file (MP4, WebM, or OGG).");
                e.target.value = '';
                return;
            }
            if (file.size > maxVideoSize) {
                setError("Video file is too large (max 100MB).");
                e.target.value = '';
                return;
            }
            
            if (uploadedMedia.filter(m => m.type === 'video').length >= maxVideos) {
                 setError(`Maximum ${maxVideos} videos reached.`);
                 e.target.value = '';
                 return;
            }
            
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = function() {
                URL.revokeObjectURL(video.src);
                if (video.duration > 60) {
                    setError("Video must be 1 minute or less.");
                    e.target.value = '';
                } else {
                    addMedia(file);
                }
            };
            video.src = URL.createObjectURL(file);
        } else {
            if (!file.type.startsWith('image/')) {
                setError("Please upload an image file (JPEG, PNG, etc.).");
                e.target.value = '';
                return;
            }
            if (file.size > maxImageSize) {
                setError("Image file is too large (max 10MB).");
                e.target.value = '';
                return;
            }
            
            if (uploadedMedia.filter(m => m.type === 'image').length >= maxImages) {
                 setError(`Maximum ${maxImages} images reached.`);
                 e.target.value = '';
                 return;
            }
            
            addMedia(file);
        }
    };

    const handleRemoveMedia = (idToRemove: string) => {
        setUploadedMedia(prev => {
            const item = prev.find(item => item.id === idToRemove);
            if (item) URL.revokeObjectURL(item.url);
            return prev.filter(item => item.id !== idToRemove);
        });
    };

    const uploadFileToFirebaseStorage = async (
        file: File, 
        stallId: string, 
        listingId: string, 
        folder: 'images' | 'videos' | 'documents'
    ): Promise<string> => {
        try {
            const fileExtension = file.name.split('.').pop();
            const fileName = `${file.name.replace(/\.[^/.]+$/, '')}_${Date.now()}.${fileExtension}`;
            const filePath = `listings/${stallId}/${listingId}/${folder}/${fileName}`;
            const fileRef = ref(storage, filePath);
            
            const snapshot = await uploadBytes(fileRef, file);
            return await getDownloadURL(snapshot.ref);
        } catch (err) {
            console.error("Error uploading file:", err);
            throw new Error("Failed to upload file. Please try again.");
        }
    };

    const handlePostSuccess = () => {
        setPostSuccess(true);
        if (typeof onPostSuccess === 'function') {
            onPostSuccess();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!userId || !userStallNumber || !stallDetails) {
            setError("User or stall details are not available. Please try again.");
            return;
        }
        
        if (isSubmitting) return;
        
        if (uploadedMedia.length === 0) {
            setError("Please add at least one image or video to your ad before posting.");
            return;
        }

        // Validate required stall details
        if (!stallDetails.businessName || !stallDetails.category) {
            setError("Stall information is incomplete. Please ensure your stall has a business name and category.");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const listingsCollectionRef = collection(db, `stalls/${userStallNumber}/listings`);
            const newListingDocRef = doc(listingsCollectionRef);
            const listingId = newListingDocRef.id;

            // Upload all media files in parallel
            const mediaDetails: ListingMedia[] = await Promise.all(
                uploadedMedia.map(async (mediaItem) => {
                    const isVideo = mediaItem.type === 'video';
                    const folder = isVideo ? 'videos' : 'images';
                    const url = await uploadFileToFirebaseStorage(
                        mediaItem.file, 
                        userStallNumber, 
                        listingId, 
                        folder
                    );

                    const mediaDetail: ListingMedia = {
                        url,
                        description: mediaItem.description || '',
                        type: isVideo ? 'video' : 'image',
                    };

                    if ('price' in mediaItem && typeof mediaItem.price === 'number') {
                        mediaDetail.price = mediaItem.price;
                    }

                    return mediaDetail;
                })
            );

            // Upload verification documents if required and not already submitted
            let verificationDocs = {};
            let status = requiresVerification ? 'pending_verification' : 'active';

            if (requiresVerification) {
                if (stallVerification.status === 'pending' || stallVerification.status === 'verified') {
                    // Documents already submitted, reuse existing URLs
                    verificationDocs = {
                        verificationStatus: stallVerification.status,
                        nidDocument: stallVerification.nidUrl,
                        titleOrLogbookDocument: stallVerification.titleUrl,
                    };
                    status = stallVerification.status === 'verified' ? 'active' : 'pending_verification';
                } else {
                    // First-time submission of documents for this stall
                    if (!nidFile) {
                        setError("National ID document is required for this category.");
                        setIsSubmitting(false);
                        return;
                    }
                    if (!titleOrLogbookFile) {
                        const docName = stallDetails.category.toLowerCase() === 'cars' ? 
                            "vehicle logbook" : "title deed";
                        setError(`${docName} document is required for this category.`);
                        setIsSubmitting(false);
                        return;
                    }

                    const [nidUrl, titleUrl] = await Promise.all([
                        uploadFileToFirebaseStorage(nidFile, userStallNumber, listingId, 'documents'),
                        uploadFileToFirebaseStorage(titleOrLogbookFile, userStallNumber, listingId, 'documents')
                    ]);

                    const stallDocRef = doc(db, 'stalls', userStallNumber);
                    await updateDoc(stallDocRef, {
                        verificationStatus: 'pending',
                        nidDocumentUrl: nidUrl,
                        titleOrLogbookDocumentUrl: titleUrl,
                    });

                    verificationDocs = {
                        verificationStatus: 'pending',
                        nidDocument: nidUrl,
                        titleOrLogbookDocument: titleUrl,
                        verifiedAt: null,
                        verifiedBy: null
                    };
                }
            }


            // Separate images and videos
            const imageUrls = mediaDetails
                .filter(m => m.type === 'image')
                .map(m => m.url);
            const videoUrls = mediaDetails
                .filter(m => m.type === 'video')
                .map(m => m.url);

            // Get the first media item for main listing details
            const firstMediaItem = mediaDetails[0];
            const mainListingDescription = firstMediaItem?.description || stallDetails.additionalDetails || '';
            const mainListingPrice = firstMediaItem?.price || null;

            // Prepare listing data with fallback values
            const newListingData = {
                listingId,
                title: stallDetails.businessName || 'Untitled Listing',
                description: mainListingDescription,
                category: stallDetails.category || 'general',
                contact: stallDetails.businessPhone || '',
                sold: false,
                images: imageUrls,
                videos: videoUrls,
                mediaDetails,
                userId,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                price: mainListingPrice,
                stallId: userStallNumber,
                status: status,
                ...verificationDocs
            };

            // Validate the final data before saving
            if (!newListingData.category) {
                throw new Error("Category is required for the listing.");
            }

            // Save to Firestore
            await setDoc(newListingDocRef, newListingData);
            
            // Reset form
            setUploadedMedia([]);
            setSelectedMediaType('');
            setNidFile(null);
            setTitleOrLogbookFile(null);
            setFileInputKey(prevKey => prevKey + 1);

            // Notify parent component
            handlePostSuccess();
        } catch (err) {
            console.error("Error adding document or uploading media:", err);
            setError(err instanceof Error ? err.message : "Failed to post listing. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const hasMedia = uploadedMedia.length > 0;

    return (
      <ContentSection>
        <h2>Post New Ad</h2>
        {error && <div style={{color: 'red', marginBottom: '15px' }}>{error}</div>}
        {postSuccess ? (
          // Display this section after a successful post
          <div style={{ textAlign: 'center' }}>
            <h3>Ad Posted Successfully!</h3>
            <p>Your ad has been submitted and is pending verification. Thank you for your post!</p>
            <Button 
              onClick={() => navigate('/')} 
              style={{ marginTop: '20px', backgroundColor: '#4CAF50', color: 'white' }}
            >
              Finish Posting & Go Home
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
          <FormSection>
             <h3>Media Upload</h3>
               {stallDetails?.stallType === 'Premium' ? (
                  <p>
                    You can add up to a maximum of **30 Images** (Item photos or general business photos) and a maximum of **10 videos** (not exceeding 100MBs & 1 minute per video).
                  </p>
                ) : (
                  <p>
                    You can add up to a maximum of **20 Images** (Item photos or general business photos) and a maximum of **3 videos** (not exceeding 100MBs & 1 minute per video).
                  </p>
                )}
            <FormGroup>
              <Label htmlFor="mediaType">Select Type of Media to Upload</Label>
              <Select 
                id="mediaType" 
                value={selectedMediaType} 
                onChange={handleMediaTypeChange} 
                disabled={isSubmitting}
              >
                {mediaTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </Select>
            </FormGroup>

            {selectedMediaType === 'item_image' && (
              <>
                <FormGroup>
                  <Label htmlFor="itemImageDescription">Item Description</Label>
                  <Input
                    type="text"
                    id="itemImageDescription"
                    value={itemImageDescription}
                    onChange={(e) => setItemImageDescription(e.target.value)}
                    placeholder="e.g., Brand new, 256GB"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="itemImagePrice">Price (Ugx)</Label>
                  <Input
                    type="number"
                    id="itemImagePrice"
                    value={itemImagePrice}
                    onChange={(e) => setItemImagePrice(e.target.value)}
                    placeholder="e.g., 1500000"
                    disabled={isSubmitting}
                    min="0"
                    step="1000"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="itemImageFile">Item Image</Label>
                  <Input
                    type="file"
                    id="itemImageFile"
                    accept="image/*"
                    onChange={(e) => handleMediaFileChange(e, 'item_image')}
                    key={`item_image-${fileInputKey}`}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              </>
            )}

            {selectedMediaType === 'video' && (
              <>
                <FormGroup>
                  <Label htmlFor="videoDescription">Video Description</Label>
                  <Input
                    type="text"
                    id="videoDescription"
                    value={videoDescription}
                    onChange={(e) => setVideoDescription(e.target.value)}
                    placeholder="e.g., Features of the item"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="videoFile">Video (Max 1 minute, 100MB)</Label>
                  <Input
                    type="file"
                    id="videoFile"
                    accept="video/mp4,video/webm,video/ogg"
                    onChange={(e) => handleMediaFileChange(e, 'video')}
                    key={`video-${fileInputKey}`}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              </>
            )}

            {selectedMediaType === 'general_image' && (
              <>
                <FormGroup>
                  <Label htmlFor="generalImageDescription">Image Description</Label>
                  <Input
                    type="text"
                    id="generalImageDescription"
                    value={generalImageDescription}
                    onChange={(e) => setGeneralImageDescription(e.target.value)}
                    placeholder="e.g., Different angle, packaging"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="generalImageFile">General Image</Label>
                  <Input
                    type="file"
                    id="generalImageFile"
                    accept="image/*"
                    onChange={(e) => handleMediaFileChange(e, 'general_image')}
                    key={`general_image-${fileInputKey}`}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              </>
            )}

            {uploadedMedia.length > 0 && (
              <MediaUploadContainer style={{marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <h4>Media to be uploaded ({uploadedMedia.length})</h4>
                {uploadedMedia.map((media) => (
                  <MediaPreview key={media.id}>
                    {media.type === 'image' ? (
                      <img 
                        src={media.url} 
                        alt={`Preview of ${media.description}`} 
                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    ) : (
                      <video 
                        src={media.url} 
                        controls 
                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    )}
                    <div className="media-info">
                      <p>{media.description}</p>
                      {media.price && <p>Ugx: {Number(media.price).toLocaleString()}</p>}
                    </div>
                    <button 
                      type="button" 
                      onClick={() => handleRemoveMedia(media.id)}
                      disabled={isSubmitting}
                    >
                      &times;
                    </button>
                  </MediaPreview>
                ))}
              </MediaUploadContainer>
            )}
          </FormSection>

          {/* Verification Documents Section for Land and Cars */}
          {requiresVerification && stallVerification.status === null && (
            <FormSection style={{marginTop: '30px', borderTop: '2px solid #eee', paddingTop: '20px'}}>
              <h3 style={{color: '#e74c3c'}}>Verification Required</h3>
              <p style={{color: '#7f8c8d', marginBottom: '20px'}}>
                For {stallDetails.category} listings, we require verification documents to ensure authenticity and protect buyers.
              </p>

              <FormGroup>
                <Label htmlFor="nidFile">
                  National ID Document (NID) *
                  <span style={{fontSize: '0.8rem', color: '#7f8c8d', display: 'block'}}>
                    Upload a clear photo or scan of your National ID (max 5MB, JPG/PNG/PDF)
                  </span>
                </Label>
                <Input
                  type="file"
                  id="nidFile"
                  accept="image/*,.pdf"
                  onChange={(e) => handleVerificationFileChange(e, 'nid')}
                  required={true}
                  disabled={isSubmitting}
                />
                {nidFile && <p>Selected: {nidFile.name} ({(nidFile.size / 1024 / 1024).toFixed(2)} MB)</p>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="titleOrLogbookFile">
                  {stallDetails.category.toLowerCase() === 'cars' ? 'Vehicle Logbook' : 'Title Deed'} *
                  <span style={{fontSize: '0.8rem', color: '#7f8c8d', display: 'block'}}>
                    Upload a clear photo or scan of your {stallDetails.category.toLowerCase() === 'cars' ? 'vehicle logbook' : 'title deed'} (max 5MB, JPG/PNG/PDF)
                  </span>
                </Label>
                <Input
                  type="file"
                  id="titleOrLogbookFile"
                  accept="image/*,.pdf"
                  onChange={(e) => handleVerificationFileChange(e, 'title_logbook')}
                  required={true}
                  disabled={isSubmitting}
                />
                {titleOrLogbookFile && (
                  <p>Selected: {titleOrLogbookFile.name} ({(titleOrLogbookFile.size / 1024 / 1024).toFixed(2)} MB)</p>
                )}
              </FormGroup>

              <div style={{
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '8px', 
                borderLeft: '4px solid #3498db',
                marginBottom: '20px'
              }}>
                <h4 style={{margin: '0 0 10px 0', color: '#2c3e50'}}>Verification Process</h4>
                <p style={{margin: '0', color: '#7f8c8d', fontSize: '0.9rem'}}>
                  Your listing will be marked as "Pending Verification" until our team reviews your documents. 
                  This usually takes 24-48 hours. Once verified, your ad will be visible to all users.
                </p>
              </div>
            </FormSection>
          )}

          {requiresVerification && stallVerification.status !== null && (
            <FormSection style={{marginTop: '30px', borderTop: '2px solid #eee', paddingTop: '20px'}}>
              <h3 style={{color: '#3498db'}}>Verification Status</h3>
              <p style={{color: '#7f8c8d', marginBottom: '20px'}}>
                  Your documents are already submitted. Your stall is currently **{stallVerification.status === 'pending' ? 'Pending Review' : 'Verified'}**.
              </p>
            </FormSection>
          )}


          <Button 
            type="submit" 
            disabled={isSubmitting || !hasMedia || (requiresVerification && stallVerification.status === null && (!nidFile || !titleOrLogbookFile))}
            style={{marginTop: '20px' }}
          >
            {isSubmitting ? 'Saving Item...' : 'Save Item'}
          </Button>
          
          {requiresVerification && (
            <p style={{fontSize: '0.8rem', color: '#7f8c8d', marginTop: '10px'}}>
              * Your ad will be published after verification approval
            </p>
          )}
        </form>
        )}
      </ContentSection>
    );
};

export default PostAd;
